// Vulnerability advisories list + Affected-by-CVE lookup.

import { useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertOctagon, Search, RotateCw } from 'lucide-react';
import { SecurityShell } from '../../components/SecurityShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import {
  type AdvisoryLookupRow,
  useAdvisories,
  useAdvisoryLookup,
  useSyncState,
  triggerSync,
} from '../../api/advisories';

function severityTone(s: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (s) {
    case 'CRITICAL':
    case 'HIGH':
      return 'danger';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'success';
    default:
      return 'neutral';
  }
}

export function Advisories() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialCve = params.get('cve') ?? '';
  const [cve, setCve] = useState(initialCve);
  const [appliedCve, setAppliedCve] = useState(initialCve);
  const { data: list, isLoading, mutate } = useAdvisories();
  const { data: sync, mutate: mutateSync } = useSyncState();
  const { data: lookup, isLoading: loadingLookup } = useAdvisoryLookup(appliedCve);

  const onSync = async () => {
    await triggerSync();
    setTimeout(() => {
      mutate();
      mutateSync();
    }, 1500);
  };

  const onRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/security/advisories/${id}`);
    }
  };

  return (
    <SecurityShell
      eyebrow="Intelligence · advisories"
      title="Vulnerability advisories"
      description="Synced hourly from OSV.dev and GHSA. We match against artifacts in the platform; click an advisory to see affected packages."
    >
      <Card>
        <CardHeader
          title="Sync status"
          description={
            sync?.last_synced_at
              ? `Last sync ${new Date(sync.last_synced_at).toLocaleString()} — ${sync.last_count} advisories upserted.`
              : 'No sync has run yet.'
          }
          actions={
            <button
              type="button"
              onClick={onSync}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Sync now
            </button>
          }
        />
        {sync?.last_error && (
          <CardBody>
            <p className="text-danger font-mono text-xs">{sync.last_error}</p>
          </CardBody>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Affected-by-CVE lookup"
          description="Paste a CVE / GHSA ID; see every project, repo, and artifact affected by it."
        />
        <div className="px-5 pb-5 flex items-end gap-2">
          <div className="flex-1">
            <label htmlFor="advisory-cve-lookup" className="block text-sm font-medium text-fg mb-1.5">CVE / GHSA</label>
            <Input
              id="advisory-cve-lookup"
              value={cve}
              onChange={(e) => setCve(e.target.value)}
              placeholder="CVE-2024-12345 or GHSA-xxxx-xxxx-xxxx"
              onKeyDown={(e) => {
                if (e.key === 'Enter') setAppliedCve(cve.trim());
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => setAppliedCve(cve.trim())}
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-danger text-white hover:bg-danger/90 text-sm font-medium"
          >
            <Search className="h-4 w-4" />
            Look up
          </button>
        </div>
        {appliedCve && (
          <CardBody>
            <LookupResults isLoading={loadingLookup} lookup={lookup} cve={appliedCve} />
          </CardBody>
        )}
      </Card>

      <Card>
        <CardHeader
          title={`All advisories (${list?.length ?? 0})`}
          description="Newest first."
          actions={isLoading ? <Spinner size="xs" /> : null}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Severity</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Advisory</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Ecosystem</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Package</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Summary</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Matches</th>
              </tr>
            </thead>
            <tbody>
              {list?.length ? (
                list.map((a) => (
                  <tr
                    key={a.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/security/advisories/${a.id}`)}
                    onKeyDown={(e) => onRowKeyDown(e, a.id)}
                    className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40 cursor-pointer focus:outline-none focus:bg-bg-hover/40"
                  >
                    <td className="px-5 py-3">
                      {a.severity ? <Badge tone={severityTone(a.severity)}>{a.severity}</Badge> : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <span className="orb-code text-xs">{a.advisory_id}</span>
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs">{a.ecosystem}</td>
                    <td className="px-5 py-3 font-mono text-xs">{a.package_name}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs max-w-md truncate">{a.summary}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {a.match_count > 0 ? (
                        <span className="text-danger font-medium">{a.match_count}</span>
                      ) : (
                        '0'
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                    <AlertOctagon className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                    No advisories synced yet. Click "Sync now" above to fetch from OSV.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </SecurityShell>
  );
}

interface LookupResultsProps {
  isLoading: boolean;
  lookup: AdvisoryLookupRow[] | undefined;
  cve: string;
}

function LookupResults({ isLoading, lookup, cve }: LookupResultsProps) {
  if (isLoading) {
    return <p className="text-fg-muted text-sm">Looking up…</p>;
  }
  if (!lookup?.length) {
    return <p className="text-fg-muted text-sm">No advisories found for "{cve}".</p>;
  }
  return (
    <>
      {lookup.map((adv) => (
        <div key={adv.id} className="border border-border rounded-md p-4 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="orb-code text-xs">{adv.advisory_id}</span>
            {adv.severity && <Badge tone={severityTone(adv.severity)}>{adv.severity}</Badge>}
            <span className="text-xs text-fg-muted">{adv.ecosystem}</span>
            <span className="text-xs text-fg-muted">·</span>
            <span className="text-xs font-mono text-fg">{adv.package_name}</span>
          </div>
          <p className="text-sm text-fg-muted mt-2">{adv.summary}</p>
          {adv.matches.length > 0 ? (
            <div className="mt-3 pl-3 border-l-2 border-danger/30">
              <p className="orb-section-title text-fg-muted">{adv.matches.length} affected artifacts</p>
              <ul className="mt-2 space-y-1">
                {adv.matches.slice(0, 10).map((m) => (
                  <li key={m.id} className="text-xs text-fg-muted">
                    <span className="text-fg">{m.project_slug}/{m.repo_slug}</span>
                    <span className="font-mono ml-2">{m.path}</span>
                    {m.version && <span className="ml-2 orb-code text-[10px]">{m.version}</span>}
                  </li>
                ))}
                {adv.matches.length > 10 && (
                  <li className="text-xs text-fg-subtle">…and {adv.matches.length - 10} more</li>
                )}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-success mt-2">No artifacts match this advisory.</p>
          )}
        </div>
      ))}
    </>
  );
}
